import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import uniqBy from "lodash.uniqby";

import { FilterTypes, QuickFilterTypes } from "@cofano/cck";

import PopoverList from "components/popover/PopoverList";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import LocationWithSubLocationsSelect, { LocationWithSubLocationsSelectOption } from "view/locations/components/selects/LocationWithSubLocationsSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import StatusSelect, { StatusSelectOption } from "view/statuses/components/selects/StatusSelect";
import FleetSelect, { FleetSelectOption } from "view/fleets/components/selects/FleetSelect";
import ProductSelect, { ProductSelectOption } from "view/products/components/select/ProductSelect";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import CargoQuantity from "view/cargos/components/quantities/CargoQuantity";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, usePermission } from "imports/Utils";

import { DossierLink, LocationLink, RelationLink, ShipLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";
import { cargoNumberWithReference } from "utils/cargoUtils";
import { productLinkWithDetails } from "utils/productUtils";

import { StatusType } from "__generated__/enums";
import { UseVoyageTableFieldsFragment } from "__generated__/view/voyages/graphql/voyage.fragments.generated";

interface VoyageTableOptions {
	refetch: () => void;
}

type MISSING_GENERIC_ERROR = {
    "❌ ERROR: You forgot to provide the <Generic> type! Usage: useVoyageTable<YourType>({ ... })": never;
};

export const useVoyageTable = <T extends UseVoyageTableFieldsFragment = never,>({
	refetch,
}: VoyageTableOptions & ([T] extends [never] ? MISSING_GENERIC_ERROR : object)) => {

	const session = useStoreState((state) => state.session);

	const { isSkipperOrShipManager, isOperator, isSkipper, isRTR } = usePermission();

	const isSkipperOrOperator = isSkipperOrShipManager || isOperator;
	const useContainers = hasPluginPermission(session, "useContainers");
	const cargoNumberAutoUseIndex = hasPluginPermission(session, "cargoNumberAutoUseIndex");
	const voyageFields = session.settings?.fields?.voyage ?? [];

	const hasLocationFilterPermission = !isSkipperOrShipManager || (hasPluginPermission(session, "availabilityExceptionSelectMasterdataLocations") || hasPluginPermission(session, "availabilityExceptionSelectMasterdataLocations"));

	const renderLocationFilterValue = (value: LocationWithSubLocationsSelectOption | LocationSelectOption[]) => {
		const locations = Array.isArray(value) ? value : value?.locations ?? [];
		return locations.map(option => option.description).join(", ");
	};

	return {
		getSearchInput: (filter: Record<string, unknown>) => {

			const locationFilter = (raw: unknown) => {
				const options = Array.isArray(raw) ? raw as LocationSelectOption[] : (raw as LocationWithSubLocationsSelectOption | null)?.locations;
				const includeSubLocations = Array.isArray(raw) ? false : Boolean((raw as LocationWithSubLocationsSelectOption | null)?.includeSubLocations);

				return Array.isArray(options) && options.length > 0 ? {
					locationIds: options.map(it => it.id),
					includeSubLocations,
				} : undefined;
			};

			return {
				voyageNumbers: filter.voyageNumber ? [ filter.voyageNumber as string ] : undefined,
				externalVoyageNumbers: filter.externalVoyageNumber ? [ filter.externalVoyageNumber as string ] : undefined,
				ships: filter.ships ? (filter.ships as ShipSelectOption[]).map(it => it.id) : undefined,
				fleets: filter.fleets ? (filter.fleets as FleetSelectOption[]).map(it => it.id) : undefined,
				statuses: filter.statuses ? (filter.statuses as StatusSelectOption[]).map(it => it.id) : undefined,
				fromLocations: locationFilter(filter.from),
				toLocations: locationFilter(filter.to),
				operators: filter.creditor ? (filter.creditor as RelationSelectOption[]).map(it => it.id) : undefined,
				products: filter.products ? (filter.products as ProductSelectOption[]).map(it => it.id) : undefined,
				invoiceableWeek: filter.invoiceableWeek ? Number(filter.invoiceableWeek as string) : undefined,
				cargoFilter: filter.cargos ? filter.cargos as string : undefined,
				includeExpiredVoyages: filter.includeExpired as boolean | undefined,
				windowFromBetween: {
					from: filter.windowFromBetween ? (filter.windowFromBetween as [number | null, number | null])[0] : undefined,
					to: filter.windowFromBetween ? (filter.windowFromBetween as [number | null, number | null])[1] : undefined,
				},
				windowToBetween: {
					from: filter.windowToBetween ? (filter.windowToBetween as [number | null, number | null])[0] : undefined,
					to: filter.windowToBetween ? (filter.windowToBetween as [number | null, number | null])[1] : undefined,
				},
				isBallast: filter.isBallast as boolean | undefined,
				salesCharterNumbers: filter["salesCharterNumber.charterNumber"] ? [ filter["salesCharterNumber.charterNumber"] as string ] : undefined,
				actualWeightFilledIn: filter.actualWeightFilledIn as boolean | undefined,
				actualTimeFilledIn: filter.actualTimeFilledIn as boolean | undefined,
				customerReferences: filter.customerReferences ? [ filter.customerReferences as string ] : undefined,
				actualWindowFrom: filter.actualWindow ? (filter.actualWindow as [number | null, number | null])[0] : undefined,
				actualWindowTo: filter.actualWindow ? (filter.actualWindow as [number | null, number | null])[1] : undefined,
				customers: filter["cargoCharters.debtor"] ? (filter["cargoCharters.debtor"] as RelationSelectOption[]).map(it => it.id) : undefined,
				firstCallLocations: locationFilter(filter.firstCall),
				lastCallLocations: locationFilter(filter.lastCall),
			};
		},

		filters: {
			VoyageNumber: {
				id: "voyageNumber",
				title: intl.formatMessage({ id: "VOYAGES_VOYAGE_NUMBER" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
			},
			ExternalVoyageNumber: {
				id: "externalVoyageNumber",
				title: intl.formatMessage({ id: "EXTERNAL_VOYAGE_NUMBER" }),
				filterType: FilterTypes.TEXT,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "externalVoyageNumber"),
				renderFilterValue: (value: string) => value,
			},
			Ship: {
				id: "ships",
				title: intl.formatMessage({ id: "SHIPS" }),
				disabled: isSkipperOrShipManager,
				render: (value: ShipSelectOption[] | null, onChange: (options: ShipSelectOption[] | null) => void) => (
					<ShipSelect
						value={value}
						onChange={options => onChange(options)}
						multiple />
				),
				renderFilterValue: (value: ShipSelectOption[]) => value.map(option => option.name).join(", "),
			},
			Fleets: {
				id: "fleets",
				title: intl.formatMessage({ id: "FLEETS" }),
				render: (value: FleetSelectOption[] | null, onChange: (options: FleetSelectOption[] | null) => void) => (
					<FleetSelect
						value={value}
						onChange={options => onChange(options)}
						multiple />
				),
				disabled: !isRTR,
				renderFilterValue: (value: FleetSelectOption[]) => value.map(option => option.description).join(", "),
			},
			From: {
				id: "from",
				title: intl.formatMessage({ id: "VOYAGE_STARTLOCATION" }),
				disabled: !hasLocationFilterPermission,
				render: (value: LocationWithSubLocationsSelectOption | null, onChange: (value: LocationWithSubLocationsSelectOption | null) => void) => (
					<LocationWithSubLocationsSelect
						value={value}
						onChange={onChange}
					/>
				),
				renderFilterValue: (value: LocationWithSubLocationsSelectOption) => renderLocationFilterValue(value),
			},
			To: {
				id: "to",
				title: intl.formatMessage({ id: "VOYAGE_ENDLOCATION" }),
				disabled: !hasLocationFilterPermission,
				render: (value: LocationWithSubLocationsSelectOption | null, onChange: (value: LocationWithSubLocationsSelectOption | null) => void) => (
					<LocationWithSubLocationsSelect
						value={value}
						onChange={onChange}
					/>
				),
				renderFilterValue: (value: LocationWithSubLocationsSelectOption) => renderLocationFilterValue(value),
			},
			Cargos: {
				id: "cargos",
				title: intl.formatMessage({ id: "CARGOS" }),
				filterType: FilterTypes.TEXT,
				disabled: useContainers || !cargoNumberAutoUseIndex,
				renderFilterValue: (value: string) => value,
			},
			Products: {
				id: "products",
				title: intl.formatMessage({ id: "PRODUCTS" }),
				render: (value: ProductSelectOption[] | null, onChange: (options: ProductSelectOption[] | null) => void) => (
					<ProductSelect
						value={value}
						onChange={options => onChange(options)}
						multiple />
				),
				renderFilterValue: (value: ProductSelectOption[]) => value.map(option => option.name).join(", "),
			},
			InvoiceableWeek: {
				id: "invoiceableWeek",
				title: `${intl.formatMessage({ id: "CONTRACT_WEEK" })} #`,
				filterType: FilterTypes.NUMBER,
				disabled: !voyageFields.includes("invoiceableWeek"),
				renderFilterValue: (value: number) => value,
			},
			Creditor: {
				id: "creditor",
				title: intl.formatMessage({ id: "CREDITOR" }),
				disabled: isSkipperOrOperator,
				render: (value: RelationSelectOption[] | null, onChange: (options: RelationSelectOption[] | null) => void) => (
					<RelationSelect
						value={value}
						onChange={options => onChange(options)}
						multiple />
				),
				renderFilterValue: (value: RelationSelectOption[]) => value.map(option => option.name).join(", "),
			},
			IncludeExpired: {
				id: "includeExpired",
				title: intl.formatMessage({ id: "INCLUDE_EXPIRED" }),
				filterType: FilterTypes.BOOLEAN,
				renderFilterValue: (value: boolean) => DataFormat.booleanAsText(value),
			},
			WindowFromBetween: {
				id: "windowFromBetween",
				title: intl.formatMessage({ id: "START_WINDOW" }),
				filterType: FilterTypes.DATE_RANGE,
				renderFilterValue: (value: [Date, Date]) => <FormattedDateWindow from={value[0]} to={value[1]} format="ll" />,
			},
			WindowToBetween: {
				id: "windowToBetween",
				title: intl.formatMessage({ id: "END_WINDOW" }),
				filterType: FilterTypes.DATE_RANGE,
				renderFilterValue: (value: [Date, Date]) => <FormattedDateWindow from={value[0]} to={value[1]} format="ll" />,
			},
			IsBallast: {
				id: "isBallast",
				title: intl.formatMessage({ id: "IS_BALLAST" }),
				filterType: FilterTypes.BOOLEAN,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "isBallast"),
				renderFilterValue: (value: boolean) => DataFormat.booleanAsText(value),
			},
			Statuses: {
				id: "statuses",
				title: intl.formatMessage({ id: "STATUS" }),
				disabled: !session.settings?.plugins.voyageStatuses,
				render: (value: StatusSelectOption[] | null, onChange: (options: StatusSelectOption[] | null) => void) => (
					<StatusSelect
						statuses={session.statuses?.VOYAGE ?? []}
						value={value ?? undefined}
						onChange={options => onChange(options ?? null)}
						multiple
					/>
				),
				renderFilterValue: (value: StatusSelectOption[]) => value.map(option => option.description).join(", "),
			},
			SalesCharterNumbers: {
				id: "salesCharterNumber.charterNumber",
				title: intl.formatMessage({ id: "SALES_CHARTER_NUMBER" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
			},
			CustomerReferences: {
				id: "customerReferences",
				title: intl.formatMessage({ id: "SHIP_REFERENCE" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
			},
			ActualWindow: {
				id: "actualWindow",
				title: intl.formatMessage({ id: "ACTUAL_WINDOW" }),
				filterType: FilterTypes.DATE_RANGE,
				renderFilterValue: (value: [Date, Date]) => <FormattedDateWindow from={value[0]} to={value[1]} format="ll" />,
			},
			Customers: {
				id: "cargoCharters.debtor",
				title: intl.formatMessage({ id: "CUSTOMER" }),
				disabled: isSkipperOrOperator,
				render: (value: RelationSelectOption[] | null, onChange: (options: RelationSelectOption[] | null) => void) => (
					<RelationSelect
						value={value}
						onChange={options => onChange(options)}
						multiple />
				),
				renderFilterValue: (value: RelationSelectOption[]) => value.map(option => option.name).join(", "),
			},
			FirstCall: {
				id: "firstCall",
				title: intl.formatMessage({ id: "FIRST_CALL" }),
				render: (value: LocationWithSubLocationsSelectOption | null, onChange: (value: LocationWithSubLocationsSelectOption | null) => void) => (
					<LocationWithSubLocationsSelect
						value={value}
						onChange={onChange} />
				),
				renderFilterValue: (value: LocationWithSubLocationsSelectOption) => renderLocationFilterValue(value),
			},
			LastCall: {
				id: "lastCall",
				title: intl.formatMessage({ id: "LAST_CALL" }),
				render: (value: LocationWithSubLocationsSelectOption | null, onChange: (value: LocationWithSubLocationsSelectOption | null) => void) => (
					<LocationWithSubLocationsSelect
						value={value}
						onChange={onChange} />
				),
				renderFilterValue: (value: LocationWithSubLocationsSelectOption) => renderLocationFilterValue(value),
			},
			ActualWeightFilledIn: {
				id: "actualWeightFilledIn",
				title: intl.formatMessage({ id: "ACTUAL_WEIGHT_FILLED_IN" }),
				filterType: FilterTypes.BOOLEAN,
				renderFilterValue: (value: boolean) => DataFormat.booleanAsText(value),
			},
			ActualTimeFilledIn: {
				id: "actualTimeFilledIn",
				title: intl.formatMessage({ id: "ACTUAL_TIME_FILLED_IN" }),
				filterType: FilterTypes.BOOLEAN,
				renderFilterValue: (value: boolean) => DataFormat.booleanAsText(value),
			},
		},

		columns: {
			VoyageNumber: {
				id: "voyageNumber",
				title: intl.formatMessage({ id: "VOYAGES_VOYAGE_NUMBER" }),
				sortable: true,
				width: 200,
				filterComponent: QuickFilterTypes.TEXT,
			},
			Ship: {
				id: "ships",
				title: intl.formatMessage({ id: "VOYAGES_SHIP" }),
				sortable: true,
				sortKey: "ship.name",
				render: (row: T) => <ShipLink ship={row.ship} />,
				width: 200,
				filterComponent: !isSkipperOrShipManager ? (value: ShipSelectOption[] | null, onChange: (options: ShipSelectOption[] | null) => void) => (
					<ShipSelect
						label={intl.formatMessage({ id: "SHIPS" })}
						value={value}
						onChange={options => onChange(options)}
						multiple />
				) : undefined,
			},
			Statuses: {
				id: "statuses",
				title: intl.formatMessage({ id: "STATUS" }),
				sortable: true,
				sortKey: "lastStatusChange.status.description",
				render: (row: T) => {
					return (
						<StatusChanger
							session={session}
							entity={row}
							statusType={StatusType.Voyage}
							refetch={refetch} />
					);
				},
				disabled: !session.settings?.plugins.voyageStatuses,
				width: 200,
				filterComponent: (value: StatusSelectOption[] | null, onChange: (options: StatusSelectOption[] | null) => void) => (
					<StatusSelect
						label={intl.formatMessage({ id: "STATUS" })}
						statuses={session.statuses?.VOYAGE ?? []}
						value={value ?? undefined}
						onChange={options => onChange(options ?? null)}
						multiple
					/>
				),
			},
			PurchaseCharterType: {
				id: "type",
				title: intl.formatMessage({ id: "CHARTER_TYPE" }),
				width: 150,
				render: (row: T) => row.purchaseCharters?.[0] ? <CharterSubtypeTag type={row.purchaseCharters[0].subtype} /> : undefined,
			},
			Start: {
				id: "start",
				title: intl.formatMessage({ id: "PLANNED_WINDOW" }),
				sortable: true,
				render: (row: T) => <FormattedDateWindow from={row.start} to={row.end} withoutYear />,
				disabled: !voyageFields.includes("start") || !voyageFields.includes("end"),
				width: 225,
			},
			From: {
				id: "from",
				title: intl.formatMessage({ id: "VOYAGE_STARTLOCATION" }),
				sortable: true,
				render: (row: T) => <LocationLink location={row.from} />,
				defaultVisible: false,
				width: 250,
				filterComponent: hasLocationFilterPermission ? (value: LocationWithSubLocationsSelectOption | null, onChange: (value: LocationWithSubLocationsSelectOption | null) => void) => (
					<LocationWithSubLocationsSelect
						label={intl.formatMessage({ id: "VOYAGE_STARTLOCATION" })}
						value={value}
						onChange={onChange} />
				) : undefined,
			},
			To: {
				id: "to",
				title: intl.formatMessage({ id: "VOYAGE_ENDLOCATION" }),
				sortable: true,
				render: (row: T) => <LocationLink location={row.to} />,
				defaultVisible: false,
				width: 250,
				filterComponent: hasLocationFilterPermission ? (value: LocationWithSubLocationsSelectOption | null, onChange: (value: LocationWithSubLocationsSelectOption | null) => void) => (
					<LocationWithSubLocationsSelect
						label={intl.formatMessage({ id: "VOYAGE_ENDLOCATION" })}
						value={value}
						onChange={onChange} />
				) : undefined,
			},
			FirstCall: {
				id: "firstCall",
				title: intl.formatMessage({ id: "FIRST_CALL" }),
				render: (row: T) => <LocationLink location={row.calls?.[0]?.location} />,
				width: 200,
				filterComponent: (value: LocationWithSubLocationsSelectOption | null, onChange: (value: LocationWithSubLocationsSelectOption | null) => void) => (
					<LocationWithSubLocationsSelect
						label={intl.formatMessage({ id: "FIRST_CALL" })}
						value={value}
						onChange={onChange} />
				),
			},
			LastCall: {
				id: "lastCall",
				title: intl.formatMessage({ id: "LAST_CALL" }),
				render: (row: T) => <LocationLink location={row.calls?.[row.calls.length - 1]?.location} />,
				width: 200,
				filterComponent: (value: LocationWithSubLocationsSelectOption | null, onChange: (value: LocationWithSubLocationsSelectOption | null) => void) => (
					<LocationWithSubLocationsSelect
						label={intl.formatMessage({ id: "LAST_CALL" })}
						value={value}
						onChange={onChange} />
				),
			},
			Cargos: {
				id: "cargos",
				title: intl.formatMessage({ id: "CARGOS" }),
				disabled: useContainers,
				width: 200,
				filterComponent: cargoNumberAutoUseIndex ? QuickFilterTypes.TEXT : undefined,
				render: (row: T) => (
					<PopoverList
						list={row.cargos ?? []}
						label={intl.formatMessage({ id: "CARGOS" })}
						render={item => cargoNumberWithReference(item)}
						limit={1} />
				),

			},
			Products: {
				id: "products",
				title: intl.formatMessage({ id: "PRODUCTS" }),
				width: 200,
				render: (row: T) =>  (
					<PopoverList
						underline={true}
						list={uniqBy(row.cargos, "product.id")}
						label={intl.formatMessage({ id: "PRODUCTS" })}
						render={item => productLinkWithDetails(item.product, item.productDetails ?? "")}
						limit={1} />
				),
				filterComponent: (value: ProductSelectOption[] | null, onChange: (options: ProductSelectOption[] | null) => void) => (
					<ProductSelect
						label={intl.formatMessage({ id: "PRODUCT" })}
						value={value}
						onChange={options => onChange(options)}
						multiple />
				),
			},
			InvoiceableWeek: {
				id: "invoiceableWeek",
				title: `${intl.formatMessage({ id: "CONTRACT_WEEK" })} #`,
				sortable: true,
				disabled: !voyageFields.includes("invoiceableWeek"),
				defaultVisible: false,
				filterComponent: QuickFilterTypes.NUMBER,
			},
			Creditor: {
				id: "creditor",
				title: intl.formatMessage({ id: "CREDITOR" }),
				sortable: true,
				width: 200,
				render: (row: T) => <RelationLink relation={row.creditor} />,
				filterComponent: !isSkipperOrOperator ? (value: RelationSelectOption[] | null, onChange: (options: RelationSelectOption[] | null) => void) => (
					<RelationSelect
						label={intl.formatMessage({ id: "CREDITOR" })}
						value={value}
						onChange={options => onChange(options)}
						multiple />
				) : undefined,
			},
			Supplier: {
				id: "supplier",
				title: intl.formatMessage({ id: "SUPPLIER" }),
				width: 200,
				disabled: !isRTR,
				render: (row: T) => row.cargos &&
					<PopoverList
						list={uniqBy(row.cargos.map(cargo => cargo.supplier).filter(it => it !== null && it !== undefined), "id")}
						underline={true}
						label={intl.formatMessage({ id: "SUPPLIERS" })}
						render={item => <RelationLink relation={item} />}
						limit={1}
					/>,
			},
			ScopeInDesired: {
				id: "scopeIn.desired",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_IN_DESIRED" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeIn?.desired} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				width: 170,
			},
			ScopeInPlanned: {
				id: "scopeIn.planned",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_IN_PLANNED" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeIn?.planned} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				width: 170,
			},
			ScopeInExpected: {
				id: "scopeIn.expected",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_IN_EXPECTED" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeIn?.expected} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				defaultVisible: false,
				width: 170,
			},
			ScopeInSystemExpected: {
				id: "scopeIn.systemExpected",
				title: `${intl.formatMessage({ id: "VOYAGE_SCOPE_IN_EXPECTED" })} ${intl.formatMessage({ id: "SYSTEM" }).toLowerCase()}`,
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeIn?.systemExpected} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				defaultVisible: false,
				width: 170,
			},
			ScopeInActual: {
				id: "scopeIn.actual",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_IN_ACTUAL" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeIn?.actual} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				defaultVisible: false,
				width: 170,
			},
			ScopeInSystemActual: {
				id: "scopeIn.systemActual",
				title: `${intl.formatMessage({ id: "VOYAGE_SCOPE_IN_ACTUAL" })} ${intl.formatMessage({ id: "SYSTEM" }).toLowerCase()}`,
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeIn?.systemActual} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				defaultVisible: false,
				width: 170,
			},
			ScopeInExpectedLocation: {
				id: "scopeIn.expectedLocation",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_IN_EXPECTED_LOCATION" }),
				sortable: true,
				sortKey: "scopeIn.expectedLocation.description",
				render: (row: T) => <LocationLink location={row.scopeIn?.expectedLocation} />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				defaultVisible: false,
				width: 200,
			},
			ScopeInPlannedLocation: {
				id: "scopeIn.plannedLocation",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_IN_PLANNED_LOCATION" }),
				sortable: true,
				sortKey: "scopeIn.plannedLocation.description",
				render: (row: T) => <LocationLink location={row.scopeIn?.plannedLocation} />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				defaultVisible: false,
				width: 200,
			},
			ScopeInActualLocation: {
				id: "scopeIn.actualLocation",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_IN_ACTUAL_LOCATION" }),
				sortable: true,
				sortKey: "scopeIn.actualLocation.description",
				render: (row: T) => <LocationLink location={row.scopeIn?.actualLocation} />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn"),
				defaultVisible: false,
				width: 200,
			},
			ScopeOutDesired: {
				id: "scopeOut.desired",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_DESIRED" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeOut?.desired} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				width: 170,
			},
			ScopeOutPlanned: {
				id: "scopeOut.planned",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_PLANNED" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeOut?.planned} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				width: 170,
			},
			ScopeOutExpected: {
				id: "scopeOut.expected",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_EXPECTED" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeOut?.expected} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				defaultVisible: false,
				width: 170,
			},
			ScopeOutSystemExpected: {
				id: "scopeOut.systemExpected",
				title: `${intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_EXPECTED" })} ${intl.formatMessage({ id: "SYSTEM" }).toLowerCase()}`,
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeOut?.systemExpected} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				defaultVisible: false,
				width: 170,
			},
			ScopeOutActual: {
				id: "scopeOut.actual",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_ACTUAL" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeOut?.actual} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				defaultVisible: false,
				width: 170,
			},
			ScopeOutSystemActual: {
				id: "scopeOut.systemActual",
				title: `${intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_ACTUAL" })} ${intl.formatMessage({ id: "SYSTEM" }).toLowerCase()}`,
				sortable: true,
				render: (row: T) => <FormattedDate date={row.scopeOut?.systemActual} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				defaultVisible: false,
				width: 170,
			},
			ScopeOutExpectedLocation: {
				id: "scopeOut.expectedLocation",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_EXPECTED_LOCATION" }),
				sortable: true,
				sortKey: "scopeOut.expectedLocation.description",
				render: (row: T) => <LocationLink location={row.scopeOut?.expectedLocation} />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				defaultVisible: false,
				width: 200,
			},
			ScopeOutPlannedLocation: {
				id: "scopeOut.plannedLocation",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_PLANNED_LOCATION" }),
				sortable: true,
				sortKey: "scopeOut.plannedLocation.description",
				render: (row: T) => <LocationLink location={row.scopeOut?.plannedLocation} />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				defaultVisible: false,
				width: 200,
			},
			ScopeOutActualLocation: {
				id: "scopeOut.actualLocation",
				title: intl.formatMessage({ id: "VOYAGE_SCOPE_OUT_ACTUAL_LOCATION" }),
				sortable: true,
				sortKey: "scopeOut.actualLocation.description",
				render: (row: T) => <LocationLink location={row.scopeOut?.actualLocation} />,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut"),
				defaultVisible: false,
				width: 200,
			},
			Created: {
				id: "created",
				title: intl.formatMessage({ id: "CREATED" }),
				sortable: true,
				render: (row: T) => <FormattedDate date={row.created} withoutYear />,
				width: 170,
			},
			ExternalVoyageNumber: {
				id: "externalVoyageNumber",
				title: intl.formatMessage({ id: "EXTERNAL_VOYAGE_NUMBER" }),
				defaultVisible: false,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "externalVoyageNumber"),
				sortable: true,
				width: 170,
				filterComponent: QuickFilterTypes.TEXT,
			},
			IsBallast: {
				id: "isBallast",
				title: intl.formatMessage({ id: "IS_BALLAST" }),
				defaultVisible: false,
				disabled: !hasOptionalFieldPermission(session, Fields.VOYAGE, "isBallast"),
				render: (row: T) => DataFormat.boolean(row.isBallast),
				sortable: true,
				filterComponent: QuickFilterTypes.BOOLEAN,
			},
			ActualWeightFilledIn: {
				id: "actualWeightFilledIn",
				title: intl.formatMessage({ id: "ACTUAL_WEIGHT_FILLED_IN" }),
				defaultVisible: false,
				render: (row: T) => DataFormat.boolean(row.actualWeightFilledIn),
				filterComponent: QuickFilterTypes.BOOLEAN,
				width: 170,
			},
			ActualTimeFilledIn: {
				id: "actualTimeFilledIn",
				title: intl.formatMessage({ id: "ACTUAL_TIME_FILLED_IN" }),
				defaultVisible: false,
				render: (row: T) => DataFormat.boolean(row.actualTimeFilledIn),
				filterComponent: QuickFilterTypes.BOOLEAN,
				width: 170,
			},
			SalesCharters: {
				id: "salesCharterNumber.charterNumber",
				title: intl.formatMessage({ id: "SALES_CHARTER_NUMBER" }),
				width: 170,
				sortable: true,
				sortKey: "salesCharters.charterNumber",
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
				render: (row: T) => (
					<PopoverList
						list={uniqBy((row.cargoCharters ?? []).filter(it => it !== null), "id")}
						underline={true}
						label={intl.formatMessage({ id: "SALES_CHARTER_NUMBER" })}
						render={item => isRTR ? <DossierLink charter={item} /> : item?.charterNumber}
						limit={1}
					/>
				),
			},
			PurchaseCharters: {
				id: "purchaseCharters.customerReference",
				title: intl.formatMessage({ id: "SHIP_REFERENCE" }),
				width: 170,
				sortable: true,
				defaultVisible: false,
				render: (row: T) => {
					const references = (row.purchaseCharters ?? []).filter(c => c.customerReference && c.customerReference.trim() !== "");
					return (
						<PopoverList
							list={uniqBy(references, "id")}
							label={intl.formatMessage({ id: "SHIP_REFERENCE" })}
							render={item => item.customerReference}
							limit={1}
						/>
					);
				},
			},
			CargosQuantity: {
				id: "cargos.quantity",
				title: intl.formatMessage({ id: "QUANTITY" }),
				width: 170,
				defaultVisible: false,
				render: (row: T) => (
					<PopoverList
						list={row.cargos ?? []}
						label={intl.formatMessage({ id: "QUANTITY" })}
						render={item => <CargoQuantity cargo={item} />}
						limit={1}
						underline={true}
					/>
				),
			},
			ActualWindow: {
				id: "call.actualWindow",
				title: intl.formatMessage({ id: "ACTUAL_WINDOW" }),
				width: 225,
				sortable: true,
				sortKey: "actualStart",
				defaultVisible: false,
				render: (row: T) => (
					<FormattedDateWindow from={row.actualStart} to={row.actualEnd} withoutYear />
				),
			},
			Debtor: {
				id: "cargoCharters.debtor",
				title: intl.formatMessage({ id: "CUSTOMER" }),
				width: 225,
				defaultVisible: false,
				render: (row: T) => (
					<PopoverList
						list={(row.cargoCharters ?? []).filter(it => it !== null)}
						underline={true}
						label={intl.formatMessage({ id: "CUSTOMER" })}
						render={item => <RelationLink relation={item?.debtor} />}
						limit={1}
					/>
				),
				disabled: !isRTR,
				filterComponent: !isSkipper ? (value: RelationSelectOption[] | null, onChange: (options: RelationSelectOption[] | null) => void) => (
					<RelationSelect
						label={intl.formatMessage({ id: "CUSTOMER" })}
						value={value}
						onChange={options => onChange(options)}
						multiple />
				) : undefined,
			},
		},
	};
};

export default useVoyageTable;
